import React, { useState, useEffect } from "react";
import MainLayout from "../Themes/MainLayout";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Alert,
  CssBaseline,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { formatDateLocal } from "../utils/dateUtils";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import { ThemeProvider } from "@mui/material/styles";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TimesheetApproval = () => {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [userRole, setUserRole] = useState(null); // Initialize as null to track unset state

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const role = await fetchClientProjects(empId);
        if (role) {
          setUserRole(role);
        } else {
          setError("User has no role in any project");
        }
      } catch (error) {
        setError("Error fetching user role");
        console.error("Error in initialize:", error);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  useEffect(() => {
    if (userRole) {
      fetchApprovals();
    }
  }, [userRole]);

  const fetchClientProjects = async (currentUserId) => {
    try {
      const params = new URLSearchParams({
        page: 1,
        limit: 100,
        boId,
        empId,
        roles,
      });
      const response = await fetch(
        `${BASE_URL}/clientprojects/paginated?${params.toString()}`
      );
      const result = await response.json();
      const userProjects = result.data.filter(
        (project) =>
          project.projectmanagerid === currentUserId ||
          project.technicalleadid === currentUserId ||
          project.hrid === currentUserId
      );
      if (userProjects.length === 0) return null;
      const firstMatch = userProjects[0];
      if (firstMatch.projectmanagerid === currentUserId) {
        return "Project Manager";
      } else if (firstMatch.technicalleadid === currentUserId) {
        return "Technical Lead";
      } else if (firstMatch.hrid === currentUserId) {
        return "HR";
      }

      return null;
    } catch (error) {
      console.error("Error fetching client projects:", error);
      return null;
    }
  };


  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/timesheetTracker/approvals/${userRole}/${empId}`
      );
      if (!res.ok) throw new Error("Failed to fetch approvals");
      const data = await res.json();
      setApprovals(data); // use directly
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const fetchBreakdown = async (index, row) => {
  try {
    const res = await fetch(
      `${BASE_URL}/api/timesheetTracker/timesheet/details/${userRole}/${empId}`
    );
    const allDetails = await res.json();

    const matching = allDetails.find(
      (d) =>
        d.employee_id === row.employee_id &&
        d.bo_id === row.bo_id &&
        d.proj_id === row.proj_id
    );

    if (matching) {
      const weekStart = new Date(row.week_start_date);
      const weekEnd = new Date(row.week_end_date);

      const filteredWeekData = (matching.timesheet_data || []).filter(
        (entry) => {
          const entryDate = new Date(entry.timesheet_date);
          return entryDate >= weekStart && entryDate <= weekEnd;
        }
      );

      setApprovals((prev) => {
        const updated = [...prev];
        updated[index].timesheet_data = filteredWeekData;
        return updated;
      });
    }
  } catch (err) {
    console.error("Failed to fetch breakdown", err);
  }
};

  const handleRemarksChange = (index, remarks) => {
    setApprovals((prev) => {
      const updated = [...prev];
      updated[index].approval_remarks = remarks;
      return updated;
    });
  };

  const handleApprove = async (row) => {
    const newStatus =
      userRole === "Technical Lead"
        ? "Waiting for Manager approval"
        : userRole === "Project Manager"
        ? "Waiting for HR approval"
        : "Approved";
    try {
      const res = await fetch(
        `${BASE_URL}/api/timesheetTracker/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bo_id: row.bo_id,
            employee_id: row.employee_id,
            week_start_date: row.week_start_date,
            proj_id: row.proj_id,
            approval_status: "approved",
           // approval_status: newStatus,
            remarks: row.approval_remarks,
            approved_by: empId,
            role: userRole,
          }),
        }
      );
      
      if (!res.ok) throw new Error("Approval failed");
      setAlert({ type: "success", msg: "Timesheet submitted!" });
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlert({ type: "", msg: "" });
      }, 3000);
      
      // Refresh list
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: "Error updating approval." });
      console.error(err);
    }
  };

  const handleReject = async (row) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/timesheetTracker/reject`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bo_id: row.bo_id,
            employee_id: row.employee_id,
            week_start_date: row.week_start_date,
            proj_id: row.proj_id,
            approval_status: "Rejected",
            remarks: row.approval_remarks,
            rejected_by: empId,
            role: userRole,
          }),
        }
      );
      if (!res.ok) throw new Error("Rejection failed");
      // Refresh list
      fetchApprovals();
    } catch (err) {
      setAlert({ type: "error", msg: "Error updating rejection." });
      console.error(err);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
 <MainLayout>
       <Typography
        variant="h4"
        gutterBottom
        fontSize="25px"
        fontWeight="600"
        sx={{ mb: 4 }}
      >
        Timesheet Approvals ({userRole})
      </Typography>
      {alert.msg && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}
      {approvals.length === 0 ? (
        <Typography>No timesheets pending approval.</Typography>
      ) : (
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer
            component={Paper}
            sx={{ overflowX: "auto", width: "100%", boxSizing: "border-box" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Employee</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Client</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Project</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Week</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Total Hours</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Remarks</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvals.map((row, index) => {
                  const isExpanded = expandedRow === index;
                  return (
                    <React.Fragment
                      key={`${row.bo_id}-${row.employee_id}-${row.week_start_date}`}
                    >
                      <TableRow
                        hover
                        onClick={() => {
                          if (isExpanded) {
                            setExpandedRow(null);
                          } else {
                            if (!row.timesheet_data) {
                              fetchBreakdown(index, row);
                            }
                            setExpandedRow(index);
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: isExpanded ? "#fafafa" : "inherit",
                        }}
                      >
                        <TableCell>{row.employee_name}</TableCell>
                        <TableCell>{row.cl_name}</TableCell>
                        <TableCell>{row.proj_name}</TableCell>
                        <TableCell>
                          {row.week_start_date} - {row.week_end_date}
                        </TableCell>
                        <TableCell>{row.effort_hours}</TableCell>
                        <TableCell>{row.approval_status}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={row.approval_remarks || ""}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              handleRemarksChange(index, e.target.value)
                            }
                            placeholder="Remarks"
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: 11,
                                padding: "6px 8px",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {row.approval_status !== "Approved" &&
                          row.approval_status !== "Rejected" ? (
                            <>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApprove(row);
                                }}
                                startIcon={<CheckIcon />}
                                sx={{ fontSize: 11, marginRight: 1, px: 1.5 }}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReject(row);
                                }}
                                startIcon={<CloseIcon />}
                                sx={{ fontSize: 11, px: 1.5 }}
                              >
                                Reject
                              </Button>
                            </>
                          ) : row.approval_status === "Rejected" ? (
                            <Typography variant="caption" color="error">
                              Rejected
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="green">
                              Approved
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expandable row */}
                      {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ padding: 0 }}>
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                           // unmountOnExit
                          >
                            <Box
                              p={2}
                              sx={{
                                backgroundColor: "#e0e0e0",
                                borderTop: "1px solid #eee",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                mb={1}
                                fontWeight={600}
                                color="black"
                                fontSize="16px"
                              >
                                Timesheet Breakdown for {row.employee_name} (
                                {row.week_start_date} - {row.week_end_date})
                              </Typography>
                              <Table size="small" sx={{ mb: 1 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>
                                      <strong>Date</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>Task</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>Hours</strong>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(
                                    (row.timesheet_data || []).reduce(
                                      (acc, entry) => {
                                        const date = formatDateLocal(
                                          entry.timesheet_date
                                        );
                                        if (!acc[date]) acc[date] = [];
                                        acc[date].push(entry);
                                        return acc;
                                      },
                                      {}
                                    )
                                  ).map(([date, tasks], i) =>
                                    tasks.map((t, j) => (
                                      <TableRow
                                        key={`${date}-${j}`}
                                        sx={{
                                          backgroundColor:
                                            i % 2 === 0 ? "#fdfdfd" : "#e8e8e8",
                                        }}
                                      >
                                        <TableCell>
                                          {j === 0 ? date : ""}
                                        </TableCell>
                                        <TableCell>
                                          {t.task_description}
                                        </TableCell>
                                        <TableCell>{t.effort_hours}</TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </ThemeProvider>
      )}
    </MainLayout> 
  );
};

export default TimesheetApproval;
